import React from "react";
import { useCart } from "../../hooks/CartContext";
import { ProductSkeleton } from "../../components/common/ProductSkeleton";
import { useQuery } from "@tanstack/react-query";
import { FaShoppingCart, FaStar } from "react-icons/fa";
import { IoIosArrowRoundForward } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const NintendoSwitch = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const brand = "Nintendo";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["brand-products", brand],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/product/brands/brand?brand=${encodeURIComponent(
          brand
        )}`
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Something went wrong");
      return json;
    },
  });

  const products = data?.products || [];

  return (
    <section className="bg-black w-full py-8 md:py-12 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 mb-6">
        <h2 className="text-white font-bold text-2xl sm:text-3xl">
          Nintendo <span className="text-orange-600">Switch</span>
        </h2>

        <Link
          to="/filter"
          className="text-white text-sm sm:text-base inline-flex items-center gap-2 px-4 sm:px-6 py-2 border border-gray-600 rounded-md hover:text-red hover:border-red transition"
        >
          See All <IoIosArrowRoundForward size={20} />
        </Link>
      </div>

      {/* Slider */}
      <div className="max-w-7xl mx-auto">
        <Swiper
          modules={[Navigation, Autoplay]}
          loop
          navigation
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          breakpoints={{
            0: { slidesPerView: 1.15, spaceBetween: 12 },
            480: { slidesPerView: 2, spaceBetween: 14 },
            640: { slidesPerView: 2.3, spaceBetween: 16 },
            1024: { slidesPerView: 4, spaceBetween: 18 },
            1280: { slidesPerView: 5, spaceBetween: 20 },
          }}
          className="rounded-md"
          style={{ "--swiper-navigation-color": "#fff" }}
        >
          {/* Loading */}
          {isLoading &&
            Array.from({ length: 8 }).map((_, i) => (
              <SwiperSlide key={i}>
                <ProductSkeleton />
              </SwiperSlide>
            ))}

          {/* Error / Empty */}
          {!isLoading && (isError || products.length === 0) && (
            <SwiperSlide>
              <div className="bg-white rounded-xl p-6 text-center">
                <p className="text-gray-800 font-semibold">
                  {isError ? "Failed to load products" : "No products found"}
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  Try again or check the filter page.
                </p>
              </div>
            </SwiperSlide>
          )}

          {/* Products */}
          {!isLoading &&
            products.slice(0, 10).map((product) => {
              const soldOut = Number(product?.quantity || 0) < 1;

              return (
                <SwiperSlide key={product?._id}>
                  <div
                    onClick={() => navigate(`/product/${product?._id}`)}
                    className="bg-white rounded-xl shadow hover:shadow-lg cursor-pointer transition overflow-hidden"
                  >
                    <img
                      src={product?.images?.[0] || "/placeholder.png"}
                      alt={product?.name || "Product"}
                      className="h-44 sm:h-48 w-full object-cover bg-gray-100"
                      loading="lazy"
                    />

                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-semibold text-gray-700 truncate">
                          {product?.name}
                        </h3>
                        <span className="text-gray-500 text-xs sm:text-sm shrink-0">
                          {product?.category}
                        </span>
                      </div>

                      <div className="flex justify-between mt-2 text-sm">
                        <span className="flex items-center gap-1 text-yellow-500 font-bold">
                          <FaStar />
                          {(Number(product?.averageRating || 0)).toFixed(1)}
                        </span>

                        {/* <span className={`font-medium ${soldOut ? "text-gray-500" : "text-red"}`}>
                          {soldOut ? "Sold Out" : `${product?.quantity} left`}
                        </span> */}
                      </div>

                      <div className="flex justify-between mt-2">
                        <span className="font-bold text-gray-600">
                          ₦{Number(product?.finalPrice || 0).toLocaleString()}
                        </span>
                  {
                          product?.discount > 0 && <>  {!!product?.price && (
                          <span className="line-through text-red">
                            ₦{Number(product?.price || 0).toLocaleString()}
                          </span>
                        )}</>
                  }
                      
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (soldOut) return;
                          addToCart(product);
                        }}
                        disabled={soldOut}
                        className={`mt-4 w-full py-2 rounded-md flex items-center justify-center gap-2 transition-all
                          ${soldOut ? "bg-gray-200 text-gray-600 cursor-not-allowed" : "bg-red text-white hover:opacity-90"}`}
                      >
                        <FaShoppingCart />
                        {soldOut ? "Sold Out" : "Add to Cart"}
                      </button>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
        </Swiper>
      </div>
    </section>
  );
};

export default NintendoSwitch;
